import {
  CommandBarButton,
  DefaultButton,
  IconButton,
  Link,
  MessageBar,
  MessageBarType,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { ADDRESS_INITIAL_VALUES } from '../../constants';
import { SupplierValues } from '../../types';
import { SupplierCommonData_countries_nodes } from '../../__generated__/SupplierCommonData';
import { AddressFormFields } from './AddressFormFields';
import { useStyles } from './index.styles';

interface AddressFormProps {
  isEdit: boolean;
  countries: SupplierCommonData_countries_nodes[];
  isUpdatable: boolean | null | undefined;
}

export const AddressForm: React.FC<AddressFormProps> = ({
  isEdit,
  countries,
  isUpdatable,
}) => {
  const styles = useStyles();
  const {
    setValue,
    control,
    formState: { errors },
  } = useFormContext<SupplierValues>();

  const { fields, remove, insert } = useFieldArray({
    control,
    name: 'supplierAddressesBySupplierId',
  });

  const addresses = useWatch({
    control,
    name: 'supplierAddressesBySupplierId',
  });

  const [showAddressForm, setShowAddressForm] = useState<boolean>(true);
  const [addressIndex, setAddressIndex] = useState<number>(0);

  const addressName = `supplierAddressesBySupplierId.${addressIndex}` as const;
  const isDisabled = isUpdatable === false;

  useEffect(() => {
    setShowAddressForm(!isEdit);
    setAddressIndex(isEdit ? -1 : 0);
  }, [isEdit]);

  const handleSetPrimary = (primaryIndex: number) => {
    fields.forEach((_field, i) => {
      setValue(
        `supplierAddressesBySupplierId.${i}.isPrimary`,
        i === primaryIndex,
        { shouldDirty: true }
      );
    });
  };

  const handleDelete = (index: number) => {
    const wasTwo = fields.length === 2;
    remove(index);
    // if only one address is left, make it primary
    if (wasTwo) {
      setValue('supplierAddressesBySupplierId.0.isPrimary', true, {
        shouldDirty: true,
      });
    }
  };

  return (
    <Stack tokens={{ padding: '10px 0px' }}>
      {showAddressForm && addressIndex !== -1 ? (
        <AddressFormFields
          addressName={addressName}
          toList={() => {
            setAddressIndex(-1);
            setShowAddressForm(false);
          }}
          countries={countries}
          isUpdatable={isUpdatable}
          setValue={setValue}
        />
      ) : (
        <>
          <Stack
            horizontal
            horizontalAlign="space-between"
            verticalAlign="center"
            className={styles.defaultButtonContainer}
          >
            <Text variant="xLarge">Address</Text>
            <DefaultButton
              text="Add Address"
              iconProps={{ iconName: 'Add' }}
              onClick={() => {
                insert(0, { ...ADDRESS_INITIAL_VALUES });
                setAddressIndex(0);
                setShowAddressForm(true);
              }}
              disabled={isDisabled}
            />
          </Stack>

          {errors.supplierAddressesBySupplierId && (
            <MessageBar messageBarType={MessageBarType.error}>
              At least one address is currently invalid
            </MessageBar>
          )}

          {fields.map((field, index) => {
            const ele = addresses?.[index] ?? field;

            const formattedAddress =
              [
                ele.addressLine1,
                ele.addressLine2,
                ele.cityName,
                ele.country?.country,
                ele.stateRegion?.stateRegion,
                ele.postalCode,
              ]
                .filter(Boolean)
                .join(', ') || 'Missing Address';

            const formattedSubtext = [
              ele.telephoneNumber,
              ele.emailAddress,
              ele.webAddress,
            ]
              .filter(Boolean)
              .join(', ');

            return (
              <Stack
                horizontal
                verticalAlign="center"
                className={clsx(styles.root, styles.AddressListItem)}
                key={field.id}
              >
                <Stack className={styles.rowCell}>
                  <Link
                    className={styles.addressTypeLabel}
                    onClick={(event) => {
                      event.preventDefault();
                      setAddressIndex(index);
                      setShowAddressForm(true);
                    }}
                  >
                    {formattedAddress}
                  </Link>
                  <Text className={styles.addressSubtitle} variant="small">
                    {formattedSubtext}
                  </Text>
                </Stack>

                <Stack horizontal tokens={{ childrenGap: 50 }}>
                  {!ele.isPrimary && !isDisabled && (
                    <CommandBarButton
                      iconProps={{ iconName: 'HomeVerify' }}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSetPrimary(index);
                      }}
                      text="Set primary"
                      className={clsx(
                        styles.setPrimaryButton,
                        'setPrimaryButtonGlobal'
                      )}
                    />
                  )}
                  {!ele.isPrimary ? (
                    !isDisabled && (
                      <TooltipHost content="Delete">
                        <IconButton
                          iconProps={{ iconName: 'delete' }}
                          className={styles.deleteIcon}
                          ariaLabel="Delete"
                          onClick={() => handleDelete(index)}
                        />
                      </TooltipHost>
                    )
                  ) : (
                    <Text className={styles.primaryLabel}>Primary</Text>
                  )}
                </Stack>
              </Stack>
            );
          })}
        </>
      )}
    </Stack>
  );
};